import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CommandLineIcon,
  ServerIcon,
  CpuChipIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

interface ServiceStatus {
  name: string;
  id: string;
  status: 'running' | 'stopped' | 'starting' | 'stopping' | 'error' | 'unknown';
  pid?: number;
  ppid?: number;
  uptime?: string;
  lastCheck: string;
  command: string;
  workingDir: string;
  description: string;
  icon: React.ComponentType<any>;
  logs?: string[];
  full_command?: string;
  cpu_usage?: string;
  memory_usage?: string;
  port?: number;
  all_pids?: number[];
}


const SystemServicesManager: React.FC = () => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Initialize services configuration
  const initialServices: ServiceStatus[] = [
    {
      name: 'Frontend Development Server',
      id: 'frontend',
      status: 'unknown',
      command: 'npm run dev',
      workingDir: 'apps/frontend',
      description: 'React development server with Vite (Port 5173)',
      icon: CommandLineIcon,
      lastCheck: new Date().toISOString(),
      logs: []
    },
    {
      name: 'Backend Development Server', 
      id: 'backend',
      status: 'unknown',
      command: 'composer run dev',
      workingDir: 'apps/backend',
      description: 'Laravel development server with concurrent processes (Port 8000)',
      icon: ServerIcon,
      lastCheck: new Date().toISOString(),
      logs: []
    },
    {
      name: 'Background Workers',
      id: 'workers',
      status: 'unknown',
      command: './start-workers.sh',
      workingDir: 'apps/backend',
      description: 'Queue workers for background job processing',
      icon: CpuChipIcon,
      lastCheck: new Date().toISOString(),
      logs: []
    },
    {
      name: 'Redis Server',
      id: 'redis',
      status: 'unknown',
      command: 'brew services start redis',
      workingDir: '~',
      description: 'Redis cache and queue storage service',
      icon: CircleStackIcon,
      lastCheck: new Date().toISOString(),
      logs: []
    }
  ];

  useEffect(() => {
    setServices(initialServices);
    checkAllServicesStatus();
    
    // Set up auto-refresh if enabled
    if (autoRefresh) {
      const interval = setInterval(() => {
        checkAllServicesStatus();
      }, 10000); // Check every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const checkAllServicesStatus = async () => {
    try {
      const response = await apiFetch('/api/admin/system-services/status', {
        headers: {
          'X-Tenant-ID': '1'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServices(prev => prev.map(service => ({
          ...service,
          ...data.services.find((s: any) => s.id === service.id),
          lastCheck: new Date().toISOString()
        })));
      } else {
        // Fallback to mock status if API not available
        setServices(prev => prev.map(service => ({
          ...service,
          status: 'unknown' as const,
          lastCheck: new Date().toISOString()
        })));
      }
    } catch (error) {
      console.error('Error checking service status:', error);
      toast.error('Failed to check service status');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceAction = async (serviceId: string, action: 'start' | 'stop' | 'restart') => {
    setActionLoading(serviceId);
    
    try {
      const response = await apiFetch('/api/admin/system-services/control', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': '1'
        },
        body: JSON.stringify({
          serviceId,
          action
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Service ${action} completed`);
        
        // Update service status
        setServices(prev => prev.map(service => 
          service.id === serviceId 
            ? { ...service, status: data.status || 'unknown', logs: [...(service.logs || []), data.output || ''].slice(-50) }
            : service
        ));
        
        // Refresh status after a short delay
        setTimeout(() => {
          checkAllServicesStatus();
        }, 2000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || `Failed to ${action} service`);
      }
    } catch (error) {
      console.error(`Error ${action}ing service:`, error);
      toast.error(`Failed to ${action} service`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'running':
        return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
      case 'stopped':
        return <XCircleIcon className="w-5 h-5 text-red-400" />;
      case 'starting':
      case 'stopping':
        return <ClockIcon className="w-5 h-5 text-yellow-400 animate-spin" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'running':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      case 'stopped':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'starting':
      case 'stopping':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'error':
        return 'text-red-500 bg-red-500/20 border-red-500/30';
      default:
        return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
    }
  };

  const getStatusText = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'stopped':
        return 'Stopped';
      case 'starting':
        return 'Starting...';
      case 'stopping':
        return 'Stopping...';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading services status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">System Services</h1>
            <p className="text-slate-400">
              Monitor and control development services
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-slate-500 bg-slate-700 text-blue-500"
              />
              <span className="text-sm text-slate-300">Auto-refresh</span>
            </label>
            <button
              onClick={() => checkAllServicesStatus()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <ArrowPathIcon className="w-4 h-4" />
              <span>Refresh All</span>
            </button>
          </div>
        </div>
      </div>

      {/* Services Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {services.map((service) => (
          <div key={service.id} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <service.icon className="w-6 h-6 text-blue-400" />
              {getStatusIcon(service.status)}
            </div>
            <h3 className="font-semibold text-white text-sm mb-1">{service.name}</h3>
            <div className={`text-xs px-2 py-1 rounded-full border inline-block ${getStatusColor(service.status)}`}>
              {getStatusText(service.status)}
            </div>
            {service.pid && (
              <p className="text-xs text-slate-500 mt-2">PID: {service.pid}</p>
            )}
            {service.uptime && (
              <p className="text-xs text-slate-500">Uptime: {service.uptime}</p>
            )}
          </div>
        ))}
      </div>

      {/* Services Details */}
      <div className="space-y-4">
        {services.map((service) => (
          <div key={service.id} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <service.icon className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{service.name}</h3>
                    {getStatusIcon(service.status)}
                    <div className={`text-sm px-3 py-1 rounded-full border ${getStatusColor(service.status)}`}>
                      {getStatusText(service.status)}
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">{service.description}</p>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Command:</span>
                      <code className="block bg-slate-900/50 px-2 py-1 rounded mt-1 text-cyan-400">
                        {service.command}
                      </code>
                    </div>
                    <div>
                      <span className="text-slate-500">Working Directory:</span>
                      <code className="block bg-slate-900/50 px-2 py-1 rounded mt-1 text-cyan-400">
                        {service.workingDir}
                      </code>
                    </div>
                    {service.pid && (
                      <div>
                        <span className="text-slate-500">Process ID (PID):</span>
                        <span className="block text-white mt-1">{service.pid}</span>
                      </div>
                    )}
                    {service.ppid && (
                      <div>
                        <span className="text-slate-500">Parent PID:</span>
                        <span className="block text-white mt-1">{service.ppid}</span>
                      </div>
                    )}
                    {service.uptime && (
                      <div>
                        <span className="text-slate-500">Uptime:</span>
                        <span className="block text-white mt-1">{service.uptime}</span>
                      </div>
                    )}
                    {service.cpu_usage && (
                      <div>
                        <span className="text-slate-500">CPU Usage:</span>
                        <span className="block text-white mt-1">{service.cpu_usage}</span>
                      </div>
                    )}
                    {service.memory_usage && (
                      <div>
                        <span className="text-slate-500">Memory Usage:</span>
                        <span className="block text-white mt-1">{service.memory_usage}</span>
                      </div>
                    )}
                    {service.port && (
                      <div>
                        <span className="text-slate-500">Port:</span>
                        <span className="block text-white mt-1">{service.port}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Full Command Details */}
                  {service.full_command && service.status === 'running' && (
                    <div className="mt-4">
                      <span className="text-slate-500 text-sm">Full Command:</span>
                      <code className="block bg-slate-900/50 px-3 py-2 rounded mt-1 text-cyan-400 text-sm overflow-x-auto">
                        {service.full_command}
                      </code>
                    </div>
                  )}
                  
                  <div className="mt-3">
                    <span className="text-slate-500 text-xs">Last checked:</span>
                    <span className="text-slate-400 text-xs ml-2">
                      {new Date(service.lastCheck).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col space-y-2 min-w-0">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleServiceAction(service.id, 'start')}
                    disabled={actionLoading === service.id || service.status === 'running'}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <PlayIcon className="w-4 h-4" />
                    <span>Start</span>
                  </button>
                  <button
                    onClick={() => handleServiceAction(service.id, 'stop')}
                    disabled={actionLoading === service.id || service.status === 'stopped'}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <StopIcon className="w-4 h-4" />
                    <span>Stop</span>
                  </button>
                </div>
                <button
                  onClick={() => handleServiceAction(service.id, 'restart')}
                  disabled={actionLoading === service.id}
                  className="px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  <span>Restart</span>
                </button>
                <button
                  onClick={() => setShowLogs(showLogs === service.id ? null : service.id)}
                  className="px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <CommandLineIcon className="w-4 h-4" />
                  <span>Logs</span>
                </button>
              </div>
            </div>
            
            {/* Service Logs */}
            {showLogs === service.id && (
              <div className="mt-6 border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-white mb-3">Recent Logs</h4>
                <div className="bg-slate-900/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {service.logs && service.logs.length > 0 ? (
                    <div className="space-y-1">
                      {service.logs.map((log, index) => (
                        <div key={index} className="text-sm font-mono text-slate-300">
                          {log}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 italic">No recent logs available</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Loading Indicator */}
            {actionLoading === service.id && (
              <div className="mt-4 flex items-center space-x-2 text-blue-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                <span className="text-sm">Processing action...</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemServicesManager;